import React from 'react';
import { Flex } from '~/ui/flex/Flex';
import {
  Item,
  ItemDefinition,
  ItemValidationError,
  registry,
  RenderProps
} from '../Item';
import { ACCORDION, ACCORDION_ITEM } from '../itemIds';
import { AccordionProvider } from './AccordionStore';
import { useEditorStore } from '~/shell/Store';
import LightButton from '~/editor/buttons/LightButton';
import { AccordionLearnerBlocker } from './AccordionLearnerBlocker';

export type AccordionDefinition = ItemDefinition<typeof ACCORDION, void>;

export class Accordion extends Item<typeof ACCORDION, void> {
  protected validate(): void {
    if (!this.definition.items?.length) {
      throw new ItemValidationError(
        `An item of type [${ACCORDION}] must have at least one child item`
      );
    }

    if (this.definition.items.find(i => i.type !== ACCORDION_ITEM)) {
      throw new ItemValidationError(
        `An item of type [${ACCORDION}] can only have children of type [${ACCORDION_ITEM}]`
      );
    }
  }

  protected render({ id, children, self }: RenderProps<void>) {
    const { contentEditable, sequence, addSubItem } = useEditorStore();
    const firstItemValues = (self as Accordion).definition.items?.[0]
      ?.values as { expandAllItems?: boolean } | undefined;
    const expandAllItems = firstItemValues?.expandAllItems !== false;

    const AddNewItem = () => {
      const list = sequence!.items.find(
        (item: { id: string; type: string }) => item.type === 'LIST'
      );

      if (list && list.items && list.items.length > 0) {
        const lastItem = list.items[list.items.length - 1];

        if (lastItem) {
          const newItem = {
            ...lastItem,
            id: `${Date.now()}`,
            position: lastItem.position! + 1
          };

          newItem.title = 'Type Here';
          newItem.body = 'Type Here';

          addSubItem({
            sequenceId: sequence!.id,
            data: {
              type: 'Text',
              body: newItem.body,
              title: newItem.title,
              position: newItem.position + 1,
              parent_id: list.id
            },
            newItem: newItem
          });
        }
      }
    };

    return (
      <AccordionProvider>
        <AccordionLearnerBlocker
          accordionId={id}
          expandAllItems={expandAllItems}
        />
        <Flex
          variant="list"
          gap="md"
          style={{ zIndex: 100 }}
          data-testid="accordion"
        >
          {children}
          {contentEditable && (
            <LightButton
              data-testid="accordion-add-item-button"
              style={{
                minWidth: '140px',
                maxWidth: '140px',
                marginTop: '20px',
                zIndex: 2,
                marginLeft: 'auto',
                marginRight: 'auto'
              }}
              onClick={AddNewItem}
            >
              Add Item
            </LightButton>
          )}
        </Flex>
      </AccordionProvider>
    );
  }
}

registry[ACCORDION] = Accordion;
